/**
 * ehr#544 (PROGRESS_NOTE_10-14-V81 115 A): addenda on a signed note. The Addenda card under the
 * note (who, when, why, what) and the Add addendum window (a reason, the text, and the attest
 * line; "Sign addendum" adds it dated and signed). An addendum never changes the note.
 */
import { useState } from 'react';
import { ADDENDUM_REASONS, type NoteAddendumView } from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { Modal } from './Modal.js';

// V81 shows the note's own attest line in the Add addendum window.
export const ADDENDUM_ATTEST =
  'By signing, I attest this note accurately reflects the services provided during this session.';

export function NoteAddendaCard({
  addenda,
  when,
}: {
  addenda: NoteAddendumView[];
  when: (iso: string) => string;
}) {
  if (!addenda.length) return null;
  return (
    <section className="pn-card pn-addenda" id="pn-addenda" data-test="addenda">
      <h3>Addenda</h3>
      {addenda.map((a) => (
        <div className="pn-adrow" key={a.id}>
          <div className="pn-adh">
            <b>{a.reason}</b>
            <span>
              {a.authorName || 'Unknown'}, {when(String(a.signedAt))}
            </span>
          </div>
          <p>{a.text}</p>
        </div>
      ))}
    </section>
  );
}

export function AddAddendumModal({
  contactId,
  noteId,
  onClose,
  onAdded,
}: {
  contactId: string;
  noteId: string;
  onClose: () => void;
  onAdded: (all: NoteAddendumView[]) => void;
}) {
  const [reason, setReason] = useState<(typeof ADDENDUM_REASONS)[number]>(ADDENDUM_REASONS[0]);
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const sign = () => {
    if (!text.trim()) {
      setErr('Write the addendum first.');
      return;
    }
    setBusy(true);
    setErr('');
    api
      .post<NoteAddendumView[]>(`/contacts/${contactId}/progress-notes/${noteId}/addenda`, {
        reason,
        text: text.trim(),
      })
      .then((all) => onAdded(all))
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'The addendum didn’t save.'),
      )
      .finally(() => setBusy(false));
  };
  return (
    <Modal
      title="Add addendum"
      onClose={onClose}
      className="pn-addendum-modal"
      actions={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={sign} disabled={busy}>
            Sign addendum
          </button>
        </>
      }
    >
      <div className="pn-sd-f">
        <span>Reason</span>
        <div className="pn-seg" role="group" aria-label="Reason">
          {ADDENDUM_REASONS.map((r) => (
            <button key={r} type="button" aria-pressed={r === reason} onClick={() => setReason(r)}>
              {r}
            </button>
          ))}
        </div>
      </div>
      <label className="pn-sd-f pn-addendum-text">
        <span>Addendum</span>
        <textarea
          className="input"
          rows={4}
          maxLength={10000}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setErr('');
          }}
          autoFocus
        />
      </label>
      {err && (
        <p className="pn-inline-err" role="alert">
          {err}
        </p>
      )}
      <p className="pn-sig-attest">{ADDENDUM_ATTEST}</p>
    </Modal>
  );
}
